import { motion, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { VideoInput } from '@/components/video/video-input';
import { useTypewriter } from '@/lib/hooks/useTypewriter';
import { ScrollAnimation } from '@/components/ui/scroll-animation';
import { Sparkles } from 'lucide-react';

const outputTypes = ['Smart Notes', 'MCQs', 'Mind Maps', 'Flashcards'];

export function Hero() {
  const typedText = useTypewriter(outputTypes);
  const staticWords = ['Transform', 'Video', 'Lectures', 'Into'];
  const { scrollY } = useScroll();
  
  // Enhanced parallax effects
  const backgroundY = useTransform(scrollY, [0, 1000], [0, 300]);
  const contentY = useTransform(scrollY, [0, 1000], [0, -150]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section className="relative z-0 min-h-[calc(100vh-4rem)] overflow-hidden bg-gradient-to-b from-primary-50/50 via-surface-100/50 to-white pt-16 transition-colors duration-300 dark:from-dark-bg dark:via-dark-bg/50 dark:to-dark-bg">
      <motion.div 
        className="container relative mx-auto px-4 py-20 text-center"
        style={{ y: contentY, opacity }}
      >
        <div className="flex flex-col items-center gap-4">
          {/* Enhanced first line with sparkle effect */}
          <motion.h1 className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1 text-5xl font-medium tracking-tight md:text-6xl">
            {staticWords.map((word, index) => (
              <motion.span
                key={word}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.7,
                  delay: index * 0.15,
                  ease: [0.25, 0.1, 0.25, 1.0],
                }}
                className="inline-block bg-gradient-to-br from-primary-900 to-primary-700 bg-clip-text text-transparent dark:from-dark-text dark:to-dark-muted"
              >
                {word}
              </motion.span>
            ))}
          </motion.h1>
          
          {/* Enhanced second line with improved typewriter effect */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="relative h-20 text-5xl font-medium tracking-tight md:text-6xl"
          >
            <motion.span className="inline-block min-w-[200px] bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600 bg-clip-text text-transparent dark:from-dark-accent dark:via-primary-400 dark:to-primary-500">
              {typedText}
              <span className="animate-pulse">|</span>
            </motion.span>
            <Sparkles className="absolute -right-8 top-0 h-6 w-6 animate-pulse text-primary-400" />
          </motion.div>
        </div>

        <ScrollAnimation 
          animation="fade-up" 
          delay={0.9}
          duration={0.9}
          className="mt-8 text-xl leading-relaxed text-gray-600 dark:text-dark-muted"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 1.2 }}
            className="inline-block bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text font-medium text-transparent dark:from-dark-accent dark:to-primary-400"
          >
            AI-powered
          </motion.span>{' '}
          video to notes conversion with MCQs, mindmaps, and more
        </ScrollAnimation>

        <ScrollAnimation
          animation="scale"
          delay={1.2}
          duration={0.9}
          className="mx-auto mt-12 max-w-2xl"
        >
          <div className="relative rounded-xl bg-white/80 p-8 shadow-lg backdrop-blur-sm transition-all duration-300 hover:shadow-xl dark:bg-dark-card/80">
            <div className="absolute inset-0 -z-10 rounded-xl bg-gradient-to-r from-primary-100/20 via-transparent to-primary-100/20 opacity-80 blur-xl" />
            <VideoInput />
          </div>
        </ScrollAnimation>
      </motion.div>
      
      {/* Enhanced background elements with improved parallax */}
      <motion.div 
        className="absolute inset-0 -z-10 overflow-hidden"
        style={{ y: backgroundY }}
      >
        <motion.div 
          className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-primary-100/50 via-transparent to-transparent dark:from-dark-hover/20"
          style={{ scale: useTransform(scrollY, [0, 1000], [1, 1.2]) }}
        />
        <motion.div 
          className="absolute inset-0 bg-[linear-gradient(to_right,_var(--tw-gradient-stops))] from-transparent via-primary-300/10 to-transparent opacity-[0.15] mix-blend-multiply dark:mix-blend-plus-lighter dark:via-dark-accent/10"
          style={{ x: useTransform(scrollY, [0, 1000], [0, 100]) }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_100%_200px,rgba(59,130,246,0.1),transparent)]" />
      </motion.div>
    </section>
  );
}